import { useIdleTimer } from "react-idle-timer";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/use-toast";
import { useRef, useCallback } from "react";
const { VITE_MAX_IDLE_TIME } = import.meta.env;
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/redux/store";
import { logoutAsync } from "@/redux/authSlice";
import {
  resetBackPageCount,
  setCode,
  setCounter,
} from "@/redux/counterSlice";
import { resetInvoice } from "@/redux/invoiceSlice";
import { resetWalletInvoice } from "@/redux/walletRefundSlice";

const IdleLogoutHandler = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const logoutInProgressRef = useRef(false);
  const lastLogoutTimeRef = useRef<number>(0);

  const handleOnIdle = useCallback(() => {
    const now = Date.now();
    const isAuthenticated = Boolean(localStorage.getItem("userToken"));

    if (!isAuthenticated) return; // Do nothing if the user is not logged in

    // Prevent multiple logout calls within 5 seconds
    if (logoutInProgressRef.current || (now - lastLogoutTimeRef.current) < 5000) {
      return;
    }

    // Set flags to prevent duplicate calls
    logoutInProgressRef.current = true;
    lastLogoutTimeRef.current = now;

    try {
      dispatch(setCode(null));
      dispatch(setCounter(null));
      dispatch(resetBackPageCount());
      dispatch(resetInvoice());
      dispatch(resetWalletInvoice());
      
      dispatch(logoutAsync());

      toast({
        variant: "destructive",
        title: "Session Timeout",
        description: "Session Timeout due to inactivity.",
      });
      
    } catch (error) {
      console.error("Error during idle logout:", error);
    } finally {
      // Reset the flag after a delay to allow for navigation
      setTimeout(() => {
        logoutInProgressRef.current = false;
      }, 1000);
    }
  }, [dispatch, navigate]);
  
  const timer = VITE_MAX_IDLE_TIME * 60 * 1000;

  const idleTimer = useIdleTimer({
    timeout: timer,
    onIdle: handleOnIdle,
    debounce: 500, // Use debounce to prevent flickering
    events: ["mousemove", "keydown", "wheel", "scroll", "touchstart"],
    // Add these options to better handle edge cases
    crossTab: true, // Sync across tabs
  });

  return null;
};

export default IdleLogoutHandler;
